'use client'

import { useEffect } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { hiveFeedQueryKey } from '@/features/post/hooks/use-hive-feed'
import { postQueryKey } from '@/features/post/hooks/use-post'
import { postCommentsQueryKey } from '@/features/post/comments/hooks/use-post-comments'
import { useSocket } from './socket-provider.client'
import { RealtimeEvent, joinRoom, leaveRoom } from './socket-client'

/**
 * Joins the Socket.IO room [roomId] for the lifetime of the calling component
 * and runs [handlers] for each named realtime event while joined. Cleans up
 * (unbinds handlers + leaves the room) on unmount or when the room changes.
 *
 * The server keys rooms by bare id — a hive id for a board feed, a post id for a
 * thread — and rejoins are idempotent, so this is safe to mount on any surface
 * that shows a live board or thread.
 */
export function useJoinRoom(
  roomId: string | undefined,
  handlers: Partial<Record<string, (data: unknown) => void>>
): void {
  const socket = useSocket()

  useEffect(() => {
    if (!socket || !roomId) return

    const entries = Object.entries(handlers)
    for (const [event, handler] of entries) {
      if (handler) socket.on(event, handler)
    }

    // Join now if already connected, and (re)join on every future connect — a
    // reconnect drops server-side room membership.
    const join = () => joinRoom(socket, roomId)
    join()
    socket.on('connect', join)

    return () => {
      socket.off('connect', join)
      for (const [event, handler] of entries) {
        if (handler) socket.off(event, handler)
      }
      leaveRoom(socket, roomId)
    }
    // handlers is expected to be a stable/inline object recreated per render; we
    // intentionally depend only on socket + roomId so we don't churn the room
    // membership every render. Callers pass invalidation closures that read
    // nothing render-specific beyond queryClient (itself stable).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [socket, roomId])
}

/**
 * Board-feed liveness: while mounted on a hive page, a second author's post (or
 * an edit/delete to any post on the board) refetches this hive's feed with no
 * user action — the same `new_post` / `post_updated` / `post_deleted` fanout the
 * mobile app consumes.
 */
export function useHiveLiveness(hiveId: string | undefined): void {
  const qc = useQueryClient()
  useJoinRoom(hiveId, {
    [RealtimeEvent.NewPost]: () => {
      if (hiveId) void qc.invalidateQueries({ queryKey: hiveFeedQueryKey(hiveId) })
    },
    [RealtimeEvent.PostUpdated]: (data) => {
      if (hiveId) void qc.invalidateQueries({ queryKey: hiveFeedQueryKey(hiveId) })
      const id = postIdOf(data)
      if (id) void qc.invalidateQueries({ queryKey: postQueryKey(id) })
    },
    [RealtimeEvent.PostDeleted]: () => {
      if (hiveId) void qc.invalidateQueries({ queryKey: hiveFeedQueryKey(hiveId) })
    },
  })
}

/**
 * Thread liveness: while mounted on a post-detail page, a second user's comment
 * or reply refetches this post's comments live, and an edit/media-ready flip
 * refetches the post itself — mirroring the mobile PostDetailsBloc socket wiring.
 */
export function usePostLiveness(postId: string | undefined): void {
  const qc = useQueryClient()
  useJoinRoom(postId, {
    [RealtimeEvent.NewComment]: () => {
      if (postId) void qc.invalidateQueries({ queryKey: postCommentsQueryKey(postId) })
    },
    [RealtimeEvent.PostUpdated]: () => {
      if (postId) {
        void qc.invalidateQueries({ queryKey: postQueryKey(postId) })
        void qc.invalidateQueries({ queryKey: postCommentsQueryKey(postId) })
      }
    },
  })
}

/** Extracts a post id from a realtime payload — the full post object or a bare id string. */
function postIdOf(data: unknown): string | undefined {
  if (typeof data === 'string') return data
  if (data && typeof data === 'object' && 'id' in data) {
    const id = (data as { id?: unknown }).id
    if (typeof id === 'string') return id
  }
  return undefined
}
